// StudentPdfUploader.js
import React, { useRef, useState } from "react";
import axios from "axios";

/* ============================================
   Dropdown options
============================================ */

const TYPE_OF_WRITING_OPTIONS = [
  { value: "situational writing", label: "Situational Writing" },
  { value: "composition", label: "Continuous Writing (Composition)" },
];
const SHOW_DEBUG_JSON = false; // controls JSON dump
const AUTO_OPEN_REPORT = false; // controls window.open + Open Report button

const getJobStatusBadge = (jobStatus) => {
  const s = (jobStatus || "").toLowerCase();

  if (s === "complete") {
    return {
      label: "Complete",
      bg: "#e7f7ed",
      fg: "#136f3a",
      border: "#bfe8cf",
    };
  }
  if (s === "failed" || s === "error") {
    return {
      label: s === "failed" ? "Failed" : "Error",
      bg: "#fdecec",
      fg: "#b42318",
      border: "#f9c7c7",
    };
  }
  if (s === "queued") {
    return { label: "Queued", bg: "#eef2ff", fg: "#3730a3", border: "#c7d2fe" };
  }
  // default: processing / polling / unknown
  return { label: s ? "Processing" : "-", bg: "#fff7e6", fg: "#8a4b00", border: "#ffe0b2" };
};

function StudentPdfUploader() {
  // ---------------------------------
  // Student-facing fields
  // ---------------------------------
  const [typeOfWriting, setTypeOfWriting] = useState("composition");
  const [studentName, setStudentName] = useState("");
  const [studentRegNum, setStudentRegNum] = useState("");
  const [studentVerificationCode, setStudentVerificationCode] = useState("");

  // ---------------------------------
  // Upload + job state
  // ---------------------------------
  const [selectedFile, setSelectedFile] = useState(null);
  // ---------------------------------
  // Upload mode + multi-image state
  // ---------------------------------
  const [uploadMode, setUploadMode] = useState("pdf"); // "pdf" | "images"
  const [selectedFiles, setSelectedFiles] = useState([]); // images
  const [status, setStatus] = useState("");
  const [jobId, setJobId] = useState("");
  const [jobStatus, setJobStatus] = useState("");
  const [jobResult, setJobResult] = useState(null);
  const [reportUrl, setReportUrl] = useState("");

  const pollingRef = useRef(null);

  const clearPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const startPollingJobStatus = (newJobId) => {
    clearPolling();
    setJobStatus("queued");
    setJobResult(null);

    pollingRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`/api/check-job-status`, {
          params: { job_id: newJobId },
        });

        const s = res.data?.status || "processing";
        const result = res.data?.result ?? null;
        setJobStatus(s);

        if (s === "complete") {
          clearPolling();

          setJobResult(result);

          const url = (result?.html_report_url || "").trim();
          setReportUrl(url);

          if (url) {
            setStatus(
              AUTO_OPEN_REPORT
                ? "✅ Job complete — opening report..."
                : "✅ Job complete — report ready."
            );
            if (AUTO_OPEN_REPORT) {
              window.open(url, "_blank", "noopener,noreferrer");
            }
          } else {
            setStatus("✅ Job complete — but no report URL returned.");
          }
        } else if (s === "failed" || s === "error") {
          clearPolling();
          const failureMessage =
            res.data?.message ||
            res.data?.error ||
            result?.message ||
            result?.error ||
            `Job ${s}`;
          setStatus(`❌ ${failureMessage}`);
        } else {
          setStatus(`⏳ Job ${s}...`);
        }
      } catch {
        setStatus("⏳ Polling... (temporary error)");
      }
    }, 2000);
  };

  const validate = () => {
    // shared fields
    if (!typeOfWriting) return "Type of writing is required.";
    if (!studentName) return "Student name is required.";
    if (!studentRegNum) return "Student register number is required.";
    if (!studentVerificationCode) return "Verification code is required.";

    // mode-specific file requirements
    if (uploadMode === "pdf") {
      if (!selectedFile) return "Please select a PDF file.";
    } else if (uploadMode === "images") {
      if (!selectedFiles || selectedFiles.length === 0)
        return "Please select at least one image file.";
    }

    return "";
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setStatus("");
    setJobId("");
    setJobStatus("");
    setJobResult(null);
    setReportUrl("");
    clearPolling();

    const errMsg = validate();
    if (errMsg) {
      setStatus(`❌ ${errMsg}`);
      return;
    }

    try {
      const formData = new FormData();

      // shared fields (same as PDF uploader)
      formData.append("type_of_writing", typeOfWriting);
      formData.append("student_name", studentName);
      formData.append("student_reg_num", studentRegNum);
      formData.append("student_verification_code", studentVerificationCode);

      let endpoint = "/api/corporate/student-upload-pdf";

      if (uploadMode === "pdf") {
        formData.append("file", selectedFile);
        setStatus("Uploading PDF...");
      } else {
        endpoint = "/api/corporate/student-upload-images";
        selectedFiles.forEach((f) => formData.append("files", f)); // IMPORTANT: key must be "files"
        setStatus("Uploading images...");
      }

      const res = await axios.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const returnedJobId = res.data?.job_id;
      if (!returnedJobId) {
        setStatus("Upload succeeded but no job_id returned.");
        return;
      }

      setJobId(returnedJobId);
      setStatus("✅ Queued. Polling job status...");
      startPollingJobStatus(returnedJobId);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Upload failed";
      setStatus(`❌ ${msg}`);
    }
  };

  return (
    <div style={{ maxWidth: 700, margin: "0 auto", padding: 16 }}>
      <h2>Student Upload — Answer Script</h2>

      <div className="upload-mode-tabs">
        <button
          type="button"
          className={`upload-mode-btn ${uploadMode === "pdf" ? "active" : ""}`}
          onClick={() => setUploadMode("pdf")}
        >
          PDF Upload
        </button>

        <button
          type="button"
          className={`upload-mode-btn ${uploadMode === "images" ? "active" : ""}`}
          onClick={() => setUploadMode("images")}
        >
          Image Upload
        </button>
      </div>

      <form onSubmit={handleUpload} style={{ display: "grid", gap: 12 }}>
        <label style={{ color: "#C62828", fontWeight: 700 }}>
          Type of Writing *
        </label>
        <select
          value={typeOfWriting}
          onChange={(e) => setTypeOfWriting(e.target.value)}
        >
          {TYPE_OF_WRITING_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <hr />

        <label>Student Name *</label>
        <input
          value={studentName}
          onChange={(e) => setStudentName(e.target.value)}
        />

        <label>Student Register No. *</label>
        <input
          value={studentRegNum}
          onChange={(e) => setStudentRegNum(e.target.value)}
        />

        <label>Verification Code *</label>
        <input
          value={studentVerificationCode}
          onChange={(e) => setStudentVerificationCode(e.target.value)}
        />

        <hr />

        {uploadMode === "pdf" ? (
          <>
            <label>PDF File *</label>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => {
                setSelectedFile(e.target.files?.[0] || null);
                setSelectedFiles([]); // prevent accidental mixed state
              }}
            />
          </>
        ) : (
          <>
            <label>Image Files (in order) *</label>
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                setSelectedFiles(files);
                setSelectedFile(null); // prevent accidental mixed state
              }}
            />

            {selectedFiles?.length > 0 && (
              <ol style={{ marginTop: 8 }}>
                {selectedFiles.map((f, idx) => (
                  <li key={`${f.name}-${idx}`}>{f.name}</li>
                ))}
              </ol>
            )}
          </>
        )}

        <button type="submit">
          {uploadMode === "pdf" ? "Upload PDF" : "Upload Images"}
        </button>
      </form>

      <hr />

      <div>
        <div>
          <b>Status:</b> {status || "-"}
        </div>
        <div>
          <b>Job ID:</b> {jobId || "-"}
        </div>
        {(() => {
          const b = getJobStatusBadge(jobStatus);
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <b>Job Status:</b>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "4px 10px",
                  borderRadius: 999,
                  fontSize: 13,
                  fontWeight: 700,
                  background: b.bg,
                  color: b.fg,
                  border: `1px solid ${b.border}`,
                }}
              >
                {b.label}
              </span>
            </div>
          );
        })()}

        {AUTO_OPEN_REPORT && reportUrl && (
          <div style={{ marginTop: 12 }}>
            <button
              onClick={() =>
                window.open(reportUrl, "_blank", "noopener,noreferrer")
              }
            >
              Open Report
            </button>
          </div>
        )}

        {SHOW_DEBUG_JSON && jobResult && (
          <pre style={{ whiteSpace: "pre-wrap" }}>
            {JSON.stringify(jobResult, null, 2)}
          </pre>
        )}
      </div>
    </div>
  );
}

export default StudentPdfUploader;
